import type { Quest } from '@hollowveil/shared';

// First quest - tutorial level
export const firstQuest: Quest = {
  id: 'quest_awakening',
  name: "L'Éveil",
  description: "Découvre tes nouveaux pouvoirs, récupère le cristal mystérieux et explore les deux mondes.",
  roomId: 'room_village_house',
  objectives: [
    {
      id: 'obj_crystal_real',
      description: 'Récupérer le Cristal Mystérieux sur la table',
      requiredWorld: 'real',
      targetObjectId: 'obj_crystal',
      completed: false,
    },
    {
      id: 'obj_ghost_spectral',
      description: 'Parler au fantôme dans le monde spectral',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_ghost',
      completed: false,
    },
    {
      id: 'obj_find_spectral_key',
      description: 'Trouver la Clé Spectrale cachée (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_spectral_key',
      completed: false,
    },
    {
      id: 'obj_find_ancient_seed',
      description: 'Trouver la Graine Ancienne près du chaudron (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_ancient_seed',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_petting_village',
      name: 'Ami des Animaux',
      description: 'Caresse tous les animaux du village. Certains ne sont visibles que dans le monde spectral...',
      icon: '🐾',
      isOptional: true,
      isComplete: false,
      reward: 'Les animaux des deux mondes te font confiance !',
      objectives: [
        {
          id: 'obj_pet_squirrel',
          description: "Caresser l'Écureuil Curieux (monde réel)",
          requiredWorld: 'real',
          targetObjectId: 'npc_squirrel_1',
          completed: false,
        },
        {
          id: 'obj_pet_hedgehog',
          description: 'Caresser le Hérisson Fantôme (monde spectral)',
          requiredWorld: 'spectral',
          targetObjectId: 'npc_hedgehog_1',
          completed: false,
        },
        {
          id: 'obj_pet_fox',
          description: 'Caresser le Renard du Voile (monde spectral)',
          requiredWorld: 'spectral',
          targetObjectId: 'npc_fox_1',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: "Tu as complété ta première quête ! Tu maîtrises maintenant le passage entre les deux mondes et tu possèdes une Clé Spectrale.",
};

// Second quest - forest garden with new mechanics
export const secondQuest: Quest = {
  id: 'quest_forest_garden',
  name: "Le Jardin Enchanté",
  description: "Traverse le jardin, active la dalle pour ouvrir la porte secrète, trouve le trésor et offre la Graine Ancienne à l'esprit de l'arbre pour obtenir sa bénédiction.",
  roomId: 'room_forest_garden',
  objectives: [
    {
      id: 'obj_flower_spectral',
      description: 'Toucher la fleur magique dans le monde spectral',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_magic_flower',
      completed: false,
    },
    {
      id: 'obj_pressure_plate',
      description: 'Activer la plaque de pression pour ouvrir la porte',
      requiredWorld: 'real',
      targetObjectId: 'obj_pressure_plate',
      completed: false,
    },
    {
      id: 'obj_find_treasure',
      description: 'Récupérer le trésor caché derrière la porte',
      requiredWorld: 'real',
      targetObjectId: 'obj_hidden_treasure',
      completed: false,
    },
    {
      id: 'obj_ancient_tree',
      description: 'Offrir la Graine Ancienne à l\'Esprit de l\'Arbre (requiert la graine du niveau 1)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_ancient_tree_spirit',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "Tu as trouvé le trésor ancien et obtenu la Bénédiction de la Forêt ! Elle t'aidera dans les ruines.",
};

// Third quest - ruins with bridges and NPC riddle
export const thirdQuest: Quest = {
  id: 'quest_ancient_ruins',
  name: "Les Ruines Oubliées",
  description: "Traverse la barrière dans le monde spectral. Parle au Sage pour l'énigme, puis utilise la Bénédiction de la Forêt (obtenue au niveau 2) sur la porte.",
  roomId: 'room_ancient_ruins',
  objectives: [
    {
      id: 'obj_cross_bridge',
      description: 'Traverser la barrière via le monde spectral',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_bridge_end',
      completed: false,
    },
    {
      id: 'obj_sage_riddle',
      description: 'Répondre à l\'énigme du Sage',
      requiredWorld: 'real',
      targetObjectId: 'obj_sage',
      completed: false,
    },
    {
      id: 'obj_open_relic_door',
      description: 'Ouvrir la porte avec la Bénédiction (niveau 2)',
      targetObjectId: 'obj_relic_door',
      completed: false,
    },
    {
      id: 'obj_ancient_relic',
      description: 'Récupérer la relique ancienne',
      targetObjectId: 'obj_relic',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "Le Sage hoche la tête avec sagesse. 'Tu as prouvé ta valeur, jeune voyageur. Le chemin vers la vérité t'est maintenant ouvert.'",
};

// Fourth quest - Temple of Shadows with sequence puzzle
export const fourthQuest: Quest = {
  id: 'quest_shadow_temple',
  name: "Le Temple des Ombres",
  description: "Active les dalles dans le bon ordre en alternant entre les mondes. Chaque gardien te donnera un indice sur la séquence.",
  roomId: 'room_shadow_temple',
  objectives: [
    {
      id: 'obj_first_guardian',
      description: 'Parler au Premier Gardien (monde réel)',
      requiredWorld: 'real',
      targetObjectId: 'obj_guardian_1',
      completed: false,
    },
    {
      id: 'obj_second_guardian',
      description: 'Parler au Second Gardien (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_guardian_2',
      completed: false,
    },
    {
      id: 'obj_activate_sequence',
      description: 'Activer les 3 dalles dans le bon ordre',
      targetObjectId: 'obj_plate_3',
      completed: false,
    },
    {
      id: 'obj_shadow_key',
      description: 'Récupérer la Clé des Ombres',
      targetObjectId: 'obj_shadow_key',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "Les ombres te reconnaissent comme leur maître. La Clé des Ombres ouvre les passages les plus sombres.",
};

// Fifth quest - Crystal Labyrinth with collectibles
export const fifthQuest: Quest = {
  id: 'quest_crystal_labyrinth',
  name: "Le Labyrinthe Cristallin",
  description: "Collecte les 4 fragments de cristal dispersés dans les deux mondes pour reformer la Clé de Cristal et ouvrir le passage.",
  roomId: 'room_crystal_labyrinth',
  objectives: [
    {
      id: 'obj_fragment_1',
      description: 'Trouver le Fragment Rouge (monde réel)',
      requiredWorld: 'real',
      targetObjectId: 'obj_crystal_red',
      completed: false,
    },
    {
      id: 'obj_fragment_2',
      description: 'Trouver le Fragment Bleu (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_crystal_blue',
      completed: false,
    },
    {
      id: 'obj_fragment_3',
      description: 'Trouver le Fragment Vert (monde réel, derrière une porte)',
      requiredWorld: 'real',
      targetObjectId: 'obj_crystal_green',
      completed: false,
    },
    {
      id: 'obj_fragment_4',
      description: 'Trouver le Fragment Doré (monde spectral, énigme)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_crystal_gold',
      completed: false,
    },
    {
      id: 'obj_forge_key',
      description: 'Utiliser la Forge Ancienne pour créer la Clé de Cristal',
      targetObjectId: 'obj_ancient_forge',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "Les fragments fusionnent en une clé magnifique. Le cristal pulse d'une énergie pure.",
};

// Sixth quest - Forbidden Library with clue hunting
export const sixthQuest: Quest = {
  id: 'quest_forbidden_library',
  name: "La Bibliothèque Interdite",
  description: "Les secrets de la bibliothèque sont gardés par le Bibliothécaire Fantôme. Trouve les indices cachés dans les livres spectraux pour répondre à ses énigmes.",
  roomId: 'room_forbidden_library',
  objectives: [
    {
      id: 'obj_clue_book_1',
      description: 'Lire le Tome des Origines (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_book_origins',
      completed: false,
    },
    {
      id: 'obj_clue_book_2',
      description: 'Lire le Grimoire des Éléments (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_book_elements',
      completed: false,
    },
    {
      id: 'obj_riddle_librarian',
      description: 'Répondre aux 3 énigmes du Bibliothécaire',
      requiredWorld: 'real',
      targetObjectId: 'obj_librarian',
      completed: false,
    },
    {
      id: 'obj_secret_vault',
      description: 'Accéder au Coffre Secret',
      targetObjectId: 'obj_library_vault',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "Le Bibliothécaire s'incline. 'La connaissance est maintenant tienne. Utilise-la avec sagesse.'",
};

// Seventh quest - Celestial Observatory with constellation puzzle
export const seventhQuest: Quest = {
  id: 'quest_celestial_observatory',
  name: "L'Observatoire Céleste",
  description: "Aligne les orbes stellaires pour former la constellation du Voile. Les deux mondes montrent différentes parties du puzzle.",
  roomId: 'room_celestial_observatory',
  objectives: [
    {
      id: 'obj_star_map',
      description: 'Examiner la Carte Stellaire (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_star_map',
      completed: false,
    },
    {
      id: 'obj_orb_position_1',
      description: 'Placer l\'Orbe du Nord',
      targetObjectId: 'obj_pedestal_north',
      completed: false,
    },
    {
      id: 'obj_orb_position_2',
      description: 'Placer l\'Orbe du Sud',
      targetObjectId: 'obj_pedestal_south',
      completed: false,
    },
    {
      id: 'obj_orb_position_3',
      description: 'Placer l\'Orbe Central',
      targetObjectId: 'obj_pedestal_center',
      completed: false,
    },
    {
      id: 'obj_astronomer_blessing',
      description: 'Recevoir la Bénédiction de l\'Astronome',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_astronomer_ghost',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "Les étoiles s'alignent. L'Astronome te transmet la sagesse des cieux.",
};

// Eighth quest - Final Sanctuary combining all mechanics
export const eighthQuest: Quest = {
  id: 'quest_final_sanctuary',
  name: "Le Sanctuaire Final",
  description: "Le dernier défi t'attend. Utilise tout ce que tu as appris : changement de monde, énigmes, objets et séquences.",
  roomId: 'room_final_sanctuary',
  objectives: [
    {
      id: 'obj_trial_shadow',
      description: 'Réussir l\'Épreuve des Ombres',
      targetObjectId: 'obj_trial_shadow_complete',
      completed: false,
    },
    {
      id: 'obj_trial_light',
      description: 'Réussir l\'Épreuve de la Lumière',
      targetObjectId: 'obj_trial_light_complete',
      completed: false,
    },
    {
      id: 'obj_final_riddle',
      description: 'Résoudre l\'Énigme Finale',
      targetObjectId: 'obj_final_guardian',
      completed: false,
    },
    {
      id: 'obj_veil_heart',
      description: 'Atteindre le Cœur du Voile',
      targetObjectId: 'obj_veil_heart',
      completed: false,
    },
  ],
  subQuests: [
    {
      id: 'subquest_petting_tundra',
      name: 'Gardien de la Toundra',
      description: 'Caresse tous les animaux du sanctuaire pour devenir leur protecteur.',
      icon: '🐻‍❄️',
      isOptional: true,
      isComplete: false,
      reward: 'Tu es devenu le Gardien de la Toundra !',
      objectives: [
        {
          id: 'obj_pet_polar_bear',
          description: "Caresser l'Ours Polaire Ancien",
          targetObjectId: 'npc_polar_bear_1',
          completed: false,
        },
        {
          id: 'obj_pet_arctic_fox',
          description: 'Caresser le Renard Arctique',
          targetObjectId: 'npc_arctic_fox_1',
          completed: false,
        },
        {
          id: 'obj_pet_penguin_1',
          description: 'Caresser le Pingouin Empereur',
          targetObjectId: 'npc_penguin_1',
          completed: false,
        },
        {
          id: 'obj_pet_penguin_2',
          description: 'Caresser le Petit Pingouin',
          targetObjectId: 'npc_penguin_2',
          completed: false,
        },
        {
          id: 'obj_pet_seal',
          description: 'Caresser le Phoque Gardien',
          targetObjectId: 'npc_seal_1',
          completed: false,
        },
        {
          id: 'obj_pet_rabbit',
          description: 'Caresser le Lapin des Neiges',
          targetObjectId: 'npc_rabbit_1',
          completed: false,
        },
      ],
    },
  ],
  isComplete: false,
  reward: "Tu as maîtrisé les deux mondes. Le Voile n'a plus de secrets pour toi. Tu es désormais le Gardien du Voile !",
};

// Ninth quest - Tower of Ascension with tall staircase
export const ninthQuest: Quest = {
  id: 'quest_tower_ascension',
  name: "La Tour de l'Ascension",
  description: "Gravir l'immense escalier de la tour pour atteindre l'Orbe Céleste au sommet. Attention, certaines marches n'existent que dans le monde spectral!",
  roomId: 'room_tower_ascension',
  objectives: [
    {
      id: 'obj_reach_first_landing',
      description: 'Atteindre le premier palier',
      targetObjectId: 'obj_landing_1',
      completed: false,
    },
    {
      id: 'obj_reach_second_landing',
      description: 'Atteindre le second palier (monde spectral)',
      requiredWorld: 'spectral',
      targetObjectId: 'obj_landing_2',
      completed: false,
    },
    {
      id: 'obj_reach_summit',
      description: 'Atteindre le sommet de la tour',
      targetObjectId: 'obj_landing_3',
      completed: false,
    },
    {
      id: 'obj_celestial_orb',
      description: "Récupérer l'Orbe Céleste",
      targetObjectId: 'obj_celestial_orb',
      completed: false,
    },
  ],
  isComplete: false,
  reward: "L'Orbe Céleste pulse d'une lumière divine. Tu as prouvé ta persévérance en atteignant le sommet!",
};

// Export all quests as an array
export const allQuests: Quest[] = [
  firstQuest,
  secondQuest,
  thirdQuest,
  fourthQuest,
  fifthQuest,
  sixthQuest,
  seventhQuest,
  eighthQuest,
  ninthQuest,
];
